import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import type { CreatePurchaseOutNoteRequest } from '@/lib/api/purchase-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { purchaseLotApi } from '@/lib/api/purchase-lot-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useCreatePurchaseOutNote,
    usePurchaseLots,
} from '@/hooks/use-purchase-lot-query'
import { purchaseLotOption } from '@/components/lots/lot-option-builders'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    purchase_lot_id: z.string().uuid(),
    quantity: z.coerce.number().positive(),
})

const schema = z.object({
    reason: z.enum(['for_sorting', 'discard', 'adjustment']),
    notes: z.string().max(2000).optional(),
    items: z.array(itemSchema).min(1),
})

type FormData = z.infer<typeof schema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-out-notes/new',
)({
    component: Page,
})

function useDebounced(value: string, delay = 300) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

function Page() {
    const navigate = useNavigate()
    const create = useCreatePurchaseOutNote()
    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch)
    const { data: lotsData } = usePurchaseLots({
        per_page: 50,
        only_available: '1',
        search: debouncedSearch || undefined,
    })
    const lotOptions = useMemo(
        () => (lotsData?.data ?? []).map(purchaseLotOption),
        [lotsData],
    )

    // Cache lot đã quét mã vạch để inject vào Combobox — usePurchaseLots chỉ
    // trả top 50 theo search, lô scan có thể ngoài trang đầu → Combobox
    // không tìm thấy option → trigger show placeholder dù form state đúng ID.
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const mergedLotOptions = useMemo(() => {
        if (scannedLots.size === 0) return lotOptions
        const map = new Map<string, LotOption>()
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const [id, opt] of scannedLots) map.set(id, opt)
        return Array.from(map.values())
    }, [lotOptions, scannedLots])
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await purchaseLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, purchaseLotOption(l))
                return next
            })
        } catch {
            // ignore — row vẫn có ID trong form state
        }
    }

    const handleSubmit = async (value: CreatePurchaseOutNoteRequest) => {
        try {
            const res = await create.mutateAsync(value)
            const id = res.data?.id
            if (id) {
                void navigate({
                    to: '/purchase-lots/stock-out-notes/$id',
                    params: { id },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PurchaseLotStockOutForm
            purchaseLotOptions={mergedLotOptions}
            isPending={create.isPending}
            onLotSearchChange={setLotSearch}
            onLotScanned={(id) => void fetchAndCacheLot(id)}
            onCancel={() =>
                navigate({
                    to: '/purchase-lots/stock-out-notes',
                    search: { page: 1 },
                })
            }
            onSubmit={(value) => void handleSubmit(value)}
        />
    )
}

export function PurchaseLotStockOutForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    purchaseLotOptions,
    isPending,
    onLotSearchChange,
    onLotScanned,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    purchaseLotOptions: LotOption[]
    isPending: boolean
    onLotSearchChange?: (v: string) => void
    onLotScanned?: (lotId: string) => void
    onSubmit: (value: CreatePurchaseOutNoteRequest) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'discard',
            notes: '',
            items: [{ purchase_lot_id: '', quantity: 0 }],
        },
    })
    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'items',
    })
    const reason = form.watch('reason')
    // Đọc toàn bộ items để loại lô đã chọn ở row khác — tránh user chọn cùng
    // 1 lô 2 lần trong cùng PXK (approve sẽ 500 vì deduct vượt available).
    const itemsWatch = form.watch('items')
    const selectedLotIds = useMemo(
        () => (itemsWatch ?? []).map((it) => it?.purchase_lot_id ?? ''),
        [itemsWatch],
    )

    // Lô đã chọn tay được cache để không rớt khỏi trigger khi search dùng chung
    // đổi (server chỉ trả 50 dòng theo từ khoá hiện tại). Merge với options từ
    // prop để dùng chung cho cả filter lẫn tra cứu "còn X".
    const [pickedLots, setPickedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const allLotOptions = useMemo(() => {
        if (pickedLots.size === 0) return purchaseLotOptions
        const map = new Map<string, LotOption>()
        for (const opt of purchaseLotOptions) map.set(opt.value, opt)
        for (const [id, opt] of pickedLots) map.set(id, opt)
        return Array.from(map.values())
    }, [purchaseLotOptions, pickedLots])
    const cacheLot = (id: string) => {
        const opt = allLotOptions.find((o) => o.value === id)
        if (!opt) return
        setPickedLots((prev) => {
            if (prev.has(id)) return prev
            const next = new Map(prev)
            next.set(id, opt)
            return next
        })
    }
    const maxRows: number | undefined = undefined

    const submit = form.handleSubmit((data) =>
        onSubmit({
            reason: data.reason,
            notes: data.notes || undefined,
            items: data.items.map((item) => ({
                purchase_lot_id: item.purchase_lot_id,
                quantity: Number(item.quantity),
            })),
        }),
    )

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.sl_lot_back()}
                </Button>
                <h1 className="text-xl font-semibold">
                    {title ?? m.pl_pxk_new_title()}
                </h1>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 rounded-md border bg-card p-4 md:grid-cols-2">
                        <div>
                            <Label className="mb-1 block">
                                {m.pl_pxk_reason()}
                            </Label>
                            <Select
                                value={reason}
                                disabled={isEdit}
                                onValueChange={(value) =>
                                    form.setValue(
                                        'reason',
                                        value as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* Xuất đi phân loại đã chuyển sang khai lô nguồn
                                        trên PNK kho phân loại — chỉ hiện khi xem/sửa
                                        phiếu cũ còn mang lý do này. */}
                                    {form.watch('reason') === 'for_sorting' && (
                                        <SelectItem value="for_sorting">
                                            {m.pl_out_reason_for_sorting()}
                                        </SelectItem>
                                    )}
                                    <SelectItem value="discard">
                                        {m.pl_out_reason_discard()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pl_reason_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-1 block">
                                {m.pl_pnk_notes()}
                            </Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <Label>{m.lot_items_title()}</Label>
                        {fields.map((field, index) => {
                            const selectedLotId = form.watch(
                                `items.${index}.purchase_lot_id`,
                            )
                            const selectedLot = allLotOptions.find(
                                (o) => o.value === selectedLotId,
                            )
                            const available = selectedLot?.available
                            return (
                                <div
                                    key={field.id}
                                    className="grid gap-3 rounded-md border bg-card p-3 md:grid-cols-[minmax(0,1fr)_160px_auto_auto] min-w-0"
                                >
                                    <Combobox
                                        options={allLotOptions.filter((o) => {
                                            if (o.value === selectedLotId)
                                                return true
                                            return !selectedLotIds.some(
                                                (id, i) =>
                                                    i !== index &&
                                                    id === o.value,
                                            )
                                        })}
                                        value={selectedLotId}
                                        onChange={(value) => {
                                            cacheLot(value)
                                            form.setValue(
                                                `items.${index}.purchase_lot_id`,
                                                value,
                                                { shouldValidate: true },
                                            )
                                        }}
                                        onSearchChange={onLotSearchChange}
                                        shouldFilter={false}
                                        selectedOption={selectedLot}
                                        onOpenChange={
                                            onLotSearchChange
                                                ? (open) => {
                                                      if (!open)
                                                          onLotSearchChange('')
                                                  }
                                                : undefined
                                        }
                                        placeholder={m.pl_lot_picker_placeholder()}
                                        searchPlaceholder={m.pl_lot_picker_search()}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.001"
                                        {...form.register(
                                            `items.${index}.quantity`,
                                            {
                                                valueAsNumber: true,
                                            },
                                        )}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={
                                            available === undefined ||
                                            available <= 0
                                        }
                                        onClick={() => {
                                            if (available !== undefined) {
                                                form.setValue(
                                                    `items.${index}.quantity`,
                                                    available,
                                                    { shouldValidate: true },
                                                )
                                            }
                                        }}
                                    >
                                        {m.pl_pxk_take_all()}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => remove(index)}
                                    >
                                        <Trash2 className="size-4 text-destructive" />
                                    </Button>
                                </div>
                            )
                        })}

                        {/* Nút thêm/quét đặt DƯỚI danh sách lô — thêm rồi sửa
                            ngay tại chỗ, không phải kéo lên đầu form. */}
                        <div className="flex items-center gap-2">
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'purchase-lots/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    const empty = current.findIndex(
                                        (it) => !it.purchase_lot_id,
                                    )
                                    if (empty >= 0) {
                                        form.setValue(
                                            `items.${empty}.purchase_lot_id`,
                                            lotId,
                                        )
                                    } else if (
                                        maxRows === undefined ||
                                        current.length < maxRows
                                    ) {
                                        append({
                                            purchase_lot_id: lotId,
                                            quantity: 0,
                                        })
                                    } else {
                                        form.setValue(
                                            `items.${current.length - 1}.purchase_lot_id`,
                                            lotId,
                                        )
                                    }
                                    onLotScanned?.(lotId)
                                }}
                            />
                            {(maxRows === undefined ||
                                fields.length < maxRows) && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                        append({
                                            purchase_lot_id: '',
                                            quantity: 0,
                                        })
                                    }
                                >
                                    <Plus className="size-4 mr-1" />
                                    {m.lot_items_add()}
                                </Button>
                            )}
                        </div>
                    </div>

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ?? m.pl_pxk_submit()}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
